import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Typography
} from '@mui/material';
import {
    Close as RemoveIcon,
    CloudUpload as UploadIcon,
    Done as DoneIcon,
    Refresh as RetryIcon
} from '@mui/icons-material';
import { uniqueId } from 'lodash';
import { AudioAssetMimeTypes, ImageAssetMimeTypes } from '@dyl/constants/AssetConstants';
import useExitPrompt from '@dyl/hooks/useExitPrompt';
import FilePreview from './FilePreview';
import useLogic from './logic';
import useStyles from './styles';
import useDialog from "@dyl/hooks/useDialog";
import preventDefault from '@dyl/utils/preventDefault';

/**
 * @typedef Props
 * @property {(params: { didUpload: boolean }) => void} onClose
 */

/**
 * @param {Omit<import('@mui/material').DialogProps, 'onClose'> & Props} props
 */
export default function AssetUploadModal({ onClose: propsOnClose, open, type, ...other }) {
    const classes = useStyles(other);
    const [ImperativeDialog, openDialog] = useDialog();
    const {
        files, addFiles, clearFiles,
        isUploading,
        startUploading,
        allowUpload,
        isDirty, setDirty,
    } = useLogic(openDialog, type);

    useExitPrompt(isUploading);

    useEffect(() => {
        if (open) {
            clearFiles();
            setDirty(false);
        }
    }, [open]);

    const onClose = useCallback(() => {
        if (isUploading) return;

        propsOnClose({ didUpload: isDirty });
    }, [isUploading, isDirty]);

    /** @type {React.MutableRefObject<HTMLInputElement>} */
    const dropZoneRef = useRef();
    const dropZoneId = useMemo(() => uniqueId('asset-drop-zone-'), []);

    /** Handles files added by clicking the drop-zone */
    const onFileSelect = useCallback(() => {
        addFiles([...dropZoneRef.current.files]);

        // Clear the input to allow more to be added.
        dropZoneRef.current.value = '';
    }, []);

    /** Handles files dropped onto the drop-zone */
    const onFileDrop = useCallback(
        /** @param {DragEvent} event */
        event => {
            event.preventDefault();
            addFiles([...event.dataTransfer.files]);
        }, []
    );


    const typeHeaderSegment = type === 'images'
        ? 'Image'
        : type === 'item audios'
            ? 'Item Audio'
            : 'Motivational Audio';

    return (
        <Dialog
            fullWidth
            maxWidth='md'
            open={open}
            onClose={onClose}
            {...other}
        >
            <DialogTitle>
                Upload {typeHeaderSegment} Asset(s)
            </DialogTitle>

            <DialogContent className={classes.content}>
                <label
                    htmlFor={dropZoneId}
                    className={classes.dropZone}
                    onDragOver={preventDefault}
                    onDrop={onFileDrop}
                >
                    <input
                        type='file'
                        multiple
                        id={dropZoneId}
                        style={{ display: 'none' }}
                        accept={type === 'images'
                            ? ImageAssetMimeTypes.join()
                            : AudioAssetMimeTypes.join()}
                        ref={dropZoneRef}
                        onChange={onFileSelect}
                    />

                    <Typography variant='h5'>
                        Drag and drop file(s) here or click
                    </Typography>

                    <UploadIcon fontSize='large' />
                </label>

                <List className={classes.fileList}>
                    {files.length === 0 && (
                        <ListItem>
                            Files will appear here once selected.
                        </ListItem>
                    )}

                    {files.map(({ file, status, remove, setStatus }) => (
                        <React.Fragment key={file.name}>
                            <ListItem className={classes[`file_${status}`]}>
                                <FilePreview file={file} className={classes.filePreview} />

                                <ListItemText
                                    disableTypography
                                    primary={(
                                        <Typography variant='body1'>
                                            {file.name}
                                        </Typography>
                                    )}
                                    secondary={(
                                        status === 'waiting' ? (
                                            isUploading ? 'Waiting...' : null
                                        )
                                        : status === 'uploading' ? <LinearProgress />
                                        : status === 'uploaded' ? 'Done'
                                        : status === 'error' ? 'Error'
                                        : 'Status unknown'
                                    )}
                                />

                                <ListItemSecondaryAction>
                                    {status === 'waiting' ? (
                                        <IconButton edge='end' onClick={remove} size="large">
                                            <RemoveIcon />
                                        </IconButton>
                                    ) : status === 'uploaded' ? (
                                        <IconButton edge='end' onClick={remove} size="large">
                                            <DoneIcon />
                                        </IconButton>
                                    ) : status === 'error' ? (
                                        <IconButton
                                            edge='end'
                                            onClick={() => {
                                                setStatus('waiting');
                                                startUploading();
                                            }}
                                            size="large">
                                            <RetryIcon />
                                        </IconButton>

                                    ) : null }
                                </ListItemSecondaryAction>
                            </ListItem>

                            <Divider variant='inset' component='li' />
                        </React.Fragment>
                    ))}
                </List>
            </DialogContent>

            <DialogActions>
                <Button color='primary' onClick={onClose} disabled={isUploading}>
                    Close
                </Button>
                <Button color='primary' onClick={startUploading} disabled={!allowUpload}>
                    Upload
                </Button>
            </DialogActions>

            <ImperativeDialog zIndex={9003} />
        </Dialog>
    );
}